import api from '@mint-lib/api';
import { useQuery } from '@mint-lib/query-client';

import { objectKeysToSnakeCase } from '@myn/shared/src/utils';

import {
  approvalFlowsSchema,
  getMoreDetailsSchema,
  statusSchema,
  type ApprovalFlows,
  type ApprovalFlowsStatusData,
} from '../../schemas.js';
import {
  APPROVAL_FLOWS_BY_REQUESTOR_KEY,
  GET_APPROVAL_FLOW_STATUS_KEY,
  GET_APPROVAL_PROCESS_DETAILS_KEY,
  GET_USERS,
} from '../constants/apiKeys.js';
import {
  APPROVAL_FLOWS_BY_REQUESTOR,
  GET_APPROVAL_FLOW_STATUS,
  USERS,
} from '../constants/endpoints.js';

export const useApprovalFlowStatus = (
  innerObj: {
    approvalItemId: string;
    approvalItemType: string;
  },
  enabled = true,
) =>
  useQuery({
    queryKey: [GET_APPROVAL_FLOW_STATUS_KEY, innerObj.approvalItemId],
    queryFn: () => getStatus(innerObj),
    staleTime: Infinity,
    enabled,
  });

export const useFetchFlows = ({ enabled }: { enabled: boolean }) =>
  useQuery({
    queryKey: [APPROVAL_FLOWS_BY_REQUESTOR_KEY],
    queryFn: getFlows,
    enabled,
  });

export const useApprovalFlow = (innerObj: {
  approvalItemId: string;
  approvalItemType: string;
}) => {
  const approvalFlowStatusQuery = useApprovalFlowStatus(innerObj);

  const approvalFlowsQuery = useFetchFlows({
    enabled:
      approvalFlowStatusQuery.data?.userStatus === 'requestor' ||
      approvalFlowStatusQuery.data?.userStatus === 'stranger',
  });

  return {
    approvalFlowStatusQuery,
    approvalFlowsQuery,
  };
};

const getFlows = async (): Promise<ApprovalFlows> => {
  const response = await api.get(APPROVAL_FLOWS_BY_REQUESTOR);
  const flows = response.data.data.items;

  return approvalFlowsSchema.parse(flows);
};

const getStatus = async (params: {
  approvalItemId: string;
  approvalItemType: string;
}): Promise<ApprovalFlowsStatusData> => {
  const searchParams = new URLSearchParams(objectKeysToSnakeCase(params));
  const response = await api.get(GET_APPROVAL_FLOW_STATUS, {
    params: searchParams,
  });

  const status = response.data.data.items[0];
  return statusSchema.parse(status);
};

const getUsers = async () => {
  const response = await api.get(USERS);
  return response.data;
};

export const useUsers = () =>
  useQuery({
    queryKey: [GET_USERS],
    queryFn: () => getUsers(),
  });

const getApprovalProcessDetails = async (approvalProcessId: string | null) => {
  const response = await api.get(
    `/api/approval-service/approval-process/${approvalProcessId}/details`,
  );
  const result = getMoreDetailsSchema.parse(response.data.data.items[0]);
  return result;
};

export const useApprovalProcessDetails = (innerObj: {
  approvalItemId: string;
  approvalItemType: string;
}) => {
  const approvalFlowStatusQuery = useApprovalFlowStatus(innerObj);

  const approvalProcessId =
    approvalFlowStatusQuery.data?.approvalProcessId ?? null;

  return useQuery({
    queryKey: [GET_APPROVAL_PROCESS_DETAILS_KEY, approvalProcessId],
    queryFn: () => getApprovalProcessDetails(approvalProcessId),
    enabled: !!approvalProcessId,
    retry: false,
    staleTime: Infinity,
  });
};
